import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../lib/org';
import * as paymentsApi from '../api/payments';
import { formatMoney } from '../lib/payments';
import {
  FREQUENCY_LABELS,
  METHOD_LABELS,
  centsToInput,
  parseDollarsToCents,
} from '../lib/paymentsUi';
import { canRefund, refundableCents } from '../lib/refunds';
import { disputeNote, feeAdjustmentNote } from '../lib/feeAdjustments';
import Modal from '../components/Modal';
import { ErrorAlert } from '../components/ui';
import { ui, cn } from '../lib/ui';

/**
 * Org admin donation ledger: /org/donations (docs/Decisions.md D44).
 * Newest first; amounts are what the webhook recorded. Org admins refund a
 * gift here, in full or in part, within 180 days (D58); a refund made in the
 * Stripe dashboard shows here too, through the charge.refunded webhook.
 * Each gift's reconciled Stripe fee and any dispute show under its amounts (D59).
 */

const STATUS_PILL = {
  succeeded: [ui.pillSuccess, 'Succeeded'],
  pending: [ui.pillWarning, 'Pending'],
  failed: [ui.pillDanger, 'Failed'],
  partially_refunded: [ui.pillWarning, 'Partly refunded'],
  refunded: [ui.pillNeutral, 'Refunded'],
};

/** The refund dialog: amount (defaults to what is left) and an optional reason. */
function RefundDialog({ donation, onClose, onRefunded }) {
  const left = refundableCents(donation);
  const [amountText, setAmountText] = useState(centsToInput(left));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const amountCents = parseDollarsToCents(amountText);
  const valid = Number.isInteger(amountCents) && amountCents >= 1 && amountCents <= left;

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const { donation: updated } = await paymentsApi.refundDonation(donation.donationId, {
        amountCents,
        reason: reason.trim() || undefined,
      });
      onRefunded(updated);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Refund donation"
      footer={
        <>
          <button type="button" className={ui.btnSecondary} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={ui.btnDanger} onClick={submit} disabled={busy || !valid}>
            {busy ? 'Refunding...' : `Refund ${valid ? formatMoney(amountCents) : ''}`}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} />
      <p className="text-body-s">
        Up to {formatMoney(left)} can be refunded to{' '}
        {donation.donorName || donation.donorEmail || 'the donor'}. The refund comes out of your
        organization&apos;s Stripe balance. The platform fee and the payment processing fee are not
        returned.
      </p>
      <label className={ui.field}>
        <span className={ui.label}>Amount (USD)</span>
        <input
          className={ui.input}
          inputMode="decimal"
          value={amountText}
          onChange={e => setAmountText(e.target.value)}
        />
      </label>
      <label className={ui.field}>
        <span className={ui.label}>Reason (optional, kept with the refund)</span>
        <input
          className={ui.input}
          maxLength={500}
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </label>
    </Modal>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function OrgDonations() {
  const { activeOrg } = useOrg();
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const [rows, setRows] = useState([]);
  const [refunding, setRefunding] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentsApi.listDonations({ limit: 50 });
      setRows(data.donations || []);
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Could not load donations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOrgAdmin) load();
  }, [isOrgAdmin, load, activeOrg?.orgId]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const data = await paymentsApi.listDonations({ limit: 50, cursor });
      setRows(r => [...r, ...(data.donations || [])]);
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Could not load more donations');
    } finally {
      setLoadingMore(false);
    }
  }

  if (!activeOrg || !isOrgAdmin) {
    return (
      <div data-zone="console">
        <h1 className={ui.h1}>Donations</h1>
        <div className={ui.alertInfo}>Only organization administrators can view donations.</div>
      </div>
    );
  }

  const received = rows
    .filter(r => r.status === 'succeeded')
    .reduce((sum, r) => sum + (r.amounts?.netToOrgCents || 0), 0);

  function onRefunded(updated) {
    setRows(current => current.map(r => (r.donationId === updated.donationId ? updated : r)));
    setRefunding(null);
  }

  return (
    <div data-zone="console">
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className={cn(ui.h1, 'mb-0!')}>Donations</h1>
        <Link to="/org/payments" className={cn(ui.btnSecondary, ui.btnSm)}>
          Payment settings
        </Link>
      </div>

      {error && (
        <div className={ui.alertError} role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className={ui.muted} role="status">
          Loading...
        </p>
      ) : rows.length === 0 ? (
        <p className={ui.muted}>No donations yet.</p>
      ) : (
        <div className={ui.cardFlat}>
          <p className={cn(ui.muted, 'mb-3 text-body-s')}>
            Shown: {rows.length} donations. Received after fees (succeeded only):{' '}
            {formatMoney(received)}.
          </p>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Donor</th>
                  <th scope="col">Type</th>
                  <th scope="col">Donation</th>
                  <th scope="col">Charged</th>
                  <th scope="col">You receive</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const [pill, label] = STATUS_PILL[r.status] || [ui.pillNeutral, r.status];
                  return (
                    <tr key={r.donationId}>
                      <td>{formatWhen(r.createdAt)}</td>
                      <td>
                        {r.donorName || r.donorEmail || 'Guest'}
                        {r.donorName && r.donorEmail && (
                          <span className={cn(ui.hint, 'block mt-0!')}>{r.donorEmail}</span>
                        )}
                      </td>
                      <td>
                        {FREQUENCY_LABELS[r.frequency] || r.frequency},{' '}
                        {METHOD_LABELS[r.paymentMethod] || r.paymentMethod}
                      </td>
                      <td className="font-mono text-data">
                        {formatMoney(r.amounts?.donationCents)}
                      </td>
                      <td className="font-mono text-data">{formatMoney(r.amounts?.totalCents)}</td>
                      <td>
                        <span className="font-mono text-data">
                          {formatMoney(r.amounts?.netToOrgCents)}
                        </span>
                        {feeAdjustmentNote(r) ? (
                          <span className={cn(ui.hint, 'block mt-0!')}>{feeAdjustmentNote(r)}</span>
                        ) : null}
                      </td>
                      <td>
                        <span className={pill}>{label}</span>
                        {disputeNote(r) ? (
                          <span className={cn(ui.hint, 'block mt-0!')}>{disputeNote(r)}</span>
                        ) : null}
                        {r.refundedCents ? (
                          <span className={cn(ui.hint, 'block mt-0!')}>
                            {formatMoney(r.refundedCents)} refunded
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {canRefund(r) ? (
                          <button
                            type="button"
                            className={cn(ui.btnSecondary, ui.btnSm)}
                            onClick={() => setRefunding(r)}
                          >
                            Refund
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {cursor && (
            <div className="mt-4">
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading more...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
      {refunding ? (
        <RefundDialog
          donation={refunding}
          onClose={() => setRefunding(null)}
          onRefunded={onRefunded}
        />
      ) : null}
      <p className={cn(ui.hint, 'mt-4')}>
        Refund a donation for up to 180 days; refunds made in the Stripe dashboard show here too.
        The You receive column is the donation and your fee, less the platform fee and the estimated
        Stripe processing fee. Once Stripe reports its actual fee, any difference is added to (or
        credited on) your next invoice and shown under the amount; a disputed donation also carries
        Stripe&apos;s dispute fee.
      </p>
    </div>
  );
}
